// import { Text } from "@react-pdf/renderer";
// import { useMemo } from "react";

// const PdfText = ({
//   children,
//   variant,
//   bold,
//   fontWeight = "normal",
//   fontSize = 10,
//   debug = false,
//   style,
// }) => {
//   const styles = useMemo(() => {
//     let fontSizeValue = fontSize;
//     let colorValue = "#000000";
//     let fontWeightValue = fontWeight;

//     if (variant === "small") {
//       fontSizeValue = 8;
//       colorValue = "#666666";
//     }

//     if (bold) {
//       fontWeightValue = "bold";
//     }

//     return {
//       fontSize: fontSizeValue,
//       fontWeight: fontWeightValue,
//       color: colorValue,
//       ...style,
//     };
//   }, [variant, fontSize, style, bold, fontWeight]);

//   return (
//     <Text debug={debug} style={styles}>
//       {children}
//     </Text>
//   );
// };
// export default PdfText;
// PdfText.jsx
import { Text, StyleSheet } from "@react-pdf/renderer";
import { useMemo } from "react";

const COLORS = {
  primary: "#1a3a5c",
  text: "#1a1a1a",
  secondary: "#555555",
  muted: "#888888",
  accent: "#2c6fbb",
  white: "#ffffff",
  error: "#d32f2f",
  success: "#2e7d32",
};

const baseStyles = StyleSheet.create({
  // ── Size variants ──
  xs: { fontSize: 6 },
  sm: { fontSize: 7 },
  md: { fontSize: 8 },
  lg: { fontSize: 9.5 },
  xl: { fontSize: 11 },
  "2xl": { fontSize: 13 },
  "3xl": { fontSize: 15 },

  // ── Color variants ──
  default: { color: COLORS.text },
  secondary: { color: COLORS.secondary },
  muted: { color: COLORS.muted },
  primary: { color: COLORS.primary },
  accent: { color: COLORS.accent },
  white: { color: COLORS.white },
  error: { color: COLORS.error },
  success: { color: COLORS.success },

  // ── Weight ──
  normal: { fontWeight: "normal" },
  medium: { fontWeight: "medium" },
  bold: { fontWeight: "bold" },

  // ── Common modifiers ──
  italic: { fontStyle: "italic" },
  uppercase: { textTransform: "uppercase", letterSpacing: 0.5 },
  center: { textAlign: "center" },
  right: { textAlign: "right" },
  underline: { textDecoration: "underline" },
  lineThrough: { textDecoration: "line-through" },
});

/**
 * Reusable PDF text component with variant support.
 *
 * @example
 * <PdfText>Default body text</PdfText>
 * <PdfText size="lg" bold>Large bold text</PdfText>
 * <PdfText color="muted" italic>Muted italic caption</PdfText>
 * <PdfText size="sm" color="secondary" uppercase>Section label</PdfText>
 * <PdfText size="xs" color="error" bold>Required</PdfText>
 */
const PdfText = ({
  children,
  size = "md",
  color = "default",
  weight,
  bold = false,
  italic = false,
  uppercase = false,
  center = false,
  right = false,
  underline = false,
  lineThrough = false,
  lineHeight,
  mb,
  mt,
  mx,
  my,
  style = {},
  ...rest
}) => {
  const composedStyle = useMemo(() => {
    const s = [
      baseStyles[size],
      baseStyles[color],
      baseStyles[bold ? "bold" : weight || "normal"],
    ];

    if (italic) s.push(baseStyles.italic);
    if (uppercase) s.push(baseStyles.uppercase);
    if (center) s.push(baseStyles.center);
    if (right) s.push(baseStyles.right);
    if (underline) s.push(baseStyles.underline);
    if (lineThrough) s.push(baseStyles.lineThrough);

    // Spacing overrides
    const overrides = {};
    if (lineHeight) overrides.lineHeight = lineHeight;
    if (mb !== undefined) overrides.marginBottom = mb;
    if (mt !== undefined) overrides.marginTop = mt;
    if (mx !== undefined) {
      overrides.marginLeft = mx;
      overrides.marginRight = mx;
    }
    if (my !== undefined) {
      overrides.marginTop = my;
      overrides.marginBottom = my;
    }

    if (Object.keys(overrides).length > 0) s.push(overrides);
    if (style && Object.keys(style).length > 0) s.push(style);

    return s;
  }, [
    size,
    color,
    weight,
    bold,
    italic,
    uppercase,
    center,
    right,
    underline,
    lineThrough,
    lineHeight,
    mb,
    mt,
    mx,
    my,
    style,
  ]);

  return (
    <Text style={composedStyle} {...rest}>
      {children}
    </Text>
  );
};

export default PdfText;
